import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  LineChart,
  ScrollText,
  Settings,
  BookOpen,
  Shield,
  Zap,
  HelpCircle,
  ChevronsLeft,
  ChevronsRight,
  X,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { signalsService } from '../../services/signals.service';
import { BRAND } from '../../config/brand';

const SIDEBAR_COLLAPSED_KEY = `${BRAND.storagePrefix}_sidebar_collapsed`;

interface NavItem {
  key: string;
  href: string;
  icon: typeof LayoutDashboard;
  /** True when this item should match nested routes via prefix. */
  prefixMatch?: boolean;
}

const TRADE_NAV: NavItem[] = [
  { key: 'dashboard', href: '/dashboard', icon: LayoutDashboard },
  { key: 'analysis', href: '/analysis', icon: LineChart, prefixMatch: true },
  { key: 'journal', href: '/journal', icon: BookOpen, prefixMatch: true },
  { key: 'history', href: '/history', icon: ScrollText },
  { key: 'signals', href: '/signals', icon: Zap },
];

const ACCOUNT_NAV: NavItem[] = [
  { key: 'settings', href: '/settings', icon: Settings },
  { key: 'help', href: '/help', icon: HelpCircle, prefixMatch: true },
];

interface SidebarProps {
  /** When true, the sidebar renders as a mobile drawer (no width state, no collapse). */
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export default function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const isPrivileged = user?.role === 'admin' || user?.role === 'expert';

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  });

  const [pendingSignals, setPendingSignals] = useState(0);
  useEffect(() => {
    if (!isPrivileged) return;
    signalsService
      .getPendingCount()
      .then((c) => setPendingSignals(c))
      .catch(() => {});
  }, [isPrivileged]);

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  // Close mobile drawer on route change
  useEffect(() => {
    if (mobileOpen) onMobileClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const isActive = (item: NavItem) => {
    if (location.pathname === item.href) return true;
    if (item.prefixMatch && location.pathname.startsWith(item.href + '/')) return true;
    return false;
  };

  // Width classes — desktop honours collapse state, mobile drawer is always full width
  const desktopWidth = collapsed ? 'md:w-16' : 'md:w-60';

  const renderNavLink = (item: NavItem, opts?: { tour?: string }) => {
    const Icon = item.icon;
    const active = isActive(item);
    return (
      <NavLink
        key={item.key}
        to={item.href}
        data-tour={opts?.tour}
        end={item.href === '/dashboard'}
        title={collapsed ? t(`nav.${item.key}`) : undefined}
        className={`
          group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium
          transition-colors focus-visible:outline-none focus-visible:ring-2
          focus-visible:ring-accent-primary focus-visible:ring-offset-2
          focus-visible:ring-offset-bg-secondary
          ${
            active
              ? 'bg-accent-primary/10 text-accent-primary'
              : 'text-text-secondary hover:text-text-primary hover:bg-bg-tertiary'
          }
        `}
      >
        <Icon className="w-5 h-5 shrink-0" aria-hidden="true" />
        <span
          className={`flex-1 truncate ${collapsed ? 'md:hidden' : ''}`}
        >
          {t(`nav.${item.key}`)}
        </span>
        {/* Tooltip when collapsed (desktop only) */}
        {collapsed && (
          <span
            className="
              pointer-events-none absolute left-full ml-2 hidden md:group-hover:block
              whitespace-nowrap rounded-md bg-bg-elevated px-2 py-1 text-xs
              text-text-primary shadow-lg border border-border-subtle z-50
            "
          >
            {t(`nav.${item.key}`)}
          </span>
        )}
      </NavLink>
    );
  };

  const renderSection = (
    label: string,
    items: NavItem[],
    extra?: React.ReactNode,
  ) => (
    <div className="px-3">
      <p
        className={`
          px-3 pb-2 pt-3 text-[11px] font-semibold uppercase tracking-wider
          text-text-muted ${collapsed ? 'md:hidden' : ''}
        `}
      >
        {label}
      </p>
      <ul className="space-y-0.5">
        {items.map((item) => (
          <li key={item.key}>
            {renderNavLink(item, {
              tour:
                item.key === 'analysis' ||
                item.key === 'history' ||
                item.key === 'settings'
                  ? `nav-${item.key}`
                  : undefined,
            })}
          </li>
        ))}
        {extra}
      </ul>
    </div>
  );

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          aria-hidden="true"
          onClick={onMobileClose}
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden animate-fade-in"
        />
      )}

      <aside
        aria-label="Primary navigation"
        className={`
          fixed inset-y-0 left-0 z-50 flex flex-col
          bg-bg-secondary border-r border-border-subtle
          transition-[transform,width] duration-200 ease-out
          w-72 ${desktopWidth}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:static md:inset-y-auto md:h-screen md:sticky md:top-0
        `}
      >
        {/* Brand row */}
        <div className="flex items-center gap-3 px-4 h-16 border-b border-border-subtle shrink-0">
          <Link
            to="/dashboard"
            onClick={onMobileClose}
            className="flex items-center gap-2.5 min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary rounded-md"
          >
            <img src="/logo-mark.svg" alt="" aria-hidden="true" className="w-8 h-8 shrink-0" />
            <span
              className={`font-display font-bold text-lg text-text-primary truncate ${collapsed ? 'md:hidden' : ''}`}
            >
              {BRAND.name}
            </span>
          </Link>

          {/* Mobile close button */}
          <button
            type="button"
            onClick={onMobileClose}
            aria-label={t('actions.cancel')}
            className="ml-auto md:hidden p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        {/* Sections */}
        <nav className="flex-1 overflow-y-auto py-2">
          {renderSection(t('sidebar.sectionTrade'), TRADE_NAV)}
          {renderSection(t('sidebar.sectionAccount'), ACCOUNT_NAV)}

          {isPrivileged && (
            <div className="px-3 mt-2">
              <p
                className={`
                  px-3 pb-2 pt-3 text-[11px] font-semibold uppercase tracking-wider
                  text-text-muted ${collapsed ? 'md:hidden' : ''}
                `}
              >
                {t('sidebar.sectionAdmin')}
              </p>
              <ul className="space-y-0.5">
                <li>
                  <NavLink
                    to="/admin"
                    title={collapsed ? t('nav.admin') : undefined}
                    className={({ isActive: navActive }) => `
                      group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium
                      transition-colors focus-visible:outline-none focus-visible:ring-2
                      focus-visible:ring-warning focus-visible:ring-offset-2
                      focus-visible:ring-offset-bg-secondary
                      ${
                        navActive
                          ? 'bg-warning/10 text-warning'
                          : 'text-warning/70 hover:text-warning hover:bg-warning/10'
                      }
                    `}
                  >
                    <Shield className="w-5 h-5 shrink-0" aria-hidden="true" />
                    <span className={`flex-1 truncate ${collapsed ? 'md:hidden' : ''}`}>
                      {t('nav.admin')}
                    </span>
                    {pendingSignals > 0 && (
                      <span
                        aria-label={t('sidebar.pendingSignals', { count: pendingSignals })}
                        className={`
                          inline-flex items-center justify-center min-w-[20px] h-5 px-1.5
                          rounded-full bg-loss text-white text-[10px] font-bold
                          ${collapsed ? 'md:absolute md:top-1 md:right-1 md:min-w-[16px] md:h-4 md:px-1' : ''}
                        `}
                      >
                        {pendingSignals}
                      </span>
                    )}
                  </NavLink>
                </li>
              </ul>
            </div>
          )}
        </nav>

        {/* Footer: collapse toggle + version */}
        <div className="border-t border-border-subtle p-2 shrink-0">
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
            className="hidden md:flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-text-tertiary hover:text-text-primary hover:bg-bg-tertiary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary"
          >
            {collapsed ? (
              <ChevronsRight className="w-5 h-5 shrink-0" aria-hidden="true" />
            ) : (
              <>
                <ChevronsLeft className="w-5 h-5 shrink-0" aria-hidden="true" />
                <span className="flex-1 text-left truncate">
                  {t('sidebar.collapse')}
                </span>
              </>
            )}
          </button>
          <p
            className={`px-3 pt-2 pb-1 text-[10px] text-text-muted opacity-60 ${collapsed ? 'md:hidden' : ''}`}
          >
            v{__APP_VERSION__}
            {__GIT_HASH__ && ` · ${__GIT_HASH__}`}
          </p>
        </div>
      </aside>
    </>
  );
}
